import React, { useState } from 'react';
import { X, Mail, Send, AlertCircle, Info, Check, Loader, Users, Eye } from 'lucide-react';
import { customerApi, ShareScanRequest } from '../services/customerApi';
import { ApiClientError } from '../services/api';
import type { Scene } from '../types/core.types';

interface Props {
  scene: Scene;
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: (shareData: any) => void;
}

// Error Dialog Component
interface DialogProps {
  isOpen: boolean;
  title: string;
  message: string;
  variant?: 'error' | 'info';
  onClose: () => void;
}

function Dialog({ isOpen, title, message, variant = 'error', onClose }: DialogProps) {
  if (!isOpen) return null;

  const IconComponent = variant === 'info' ? Info : AlertCircle;
  const iconColor = variant === 'info' ? '#0CB4E4' : '#0CB4E4';
  
  return (
    <div className="fixed inset-0 z-[1101] overflow-y-auto">
      <div className="flex min-h-full items-center justify-center p-4">
        {/* Backdrop */}
        <div className="fixed inset-0 bg-black/70 transition-opacity" onClick={onClose} />
        
        {/* Dialog */}
        <div className="relative bg-[#161513] rounded-none shadow-xl w-full max-w-sm max-h-[90vh] overflow-y-auto transform transition-all border border-[#0CB4E4]/30">
          <div className="p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 bg-[#161513] rounded-lg">
                  <IconComponent className="w-6 h-6" style={{ color: iconColor }} />
              </div>
              <h3 className="text-lg font-semibold text-white">{title}</h3>
            </div>
            
            <p className="text-gray-300 mb-6 leading-relaxed">{message}</p>
            
            <button 
              onClick={onClose} 
              className="w-full px-4 py-3 bg-[#0CB4E4] text-[#161513] rounded-xl hover:bg-[#0CB4E4]/90 transition-colors font-medium shadow-md" 
            >
              OK
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// Utility function to extract clean error messages
function extractErrorMessage(error: unknown): string {
  // Handle ApiClientError
  if (error instanceof ApiClientError) {
    return error.message;
  }
  
  // Handle regular Error objects
  if (error instanceof Error) {
    let message = error.message;
    
    // Check if the message contains HTML (common with server error responses)
    if (message.includes('<!DOCTYPE html>') || message.includes('<html>')) {
      // Try to extract the actual error message from HTML
      const htmlMatch = message.match(/<pre>(.*?)<\/pre>/s);
      if (htmlMatch) {
        // Extract just the first line of the error, remove HTML entities
        const errorText = htmlMatch[1].split('<br>')[0].split('\n')[0];
        return errorText.replace(/&nbsp;/g, ' ').replace(/</g, '<').replace(/>/g, '>').trim();
      }
      
      // If no pre tag found, try to extract from title
      const titleMatch = message.match(/<title>(.*?)<\/title>/);
      if (titleMatch && titleMatch[1] !== 'Error') {
        return titleMatch[1];
      }
      
      // Default fallback for HTML responses
      return 'An error occurred while sharing the scene. Please try again.';
    }
    
    return message;
  }
  
  // Handle string errors
  if (typeof error === 'string') {
    return error;
  }
  
  // Fallback for unknown error types
  return 'An unexpected error occurred. Please try again.';
}

export default function ShareModal({ scene, isOpen, onClose, onSuccess }: Props) {
  const [email, setEmail] = useState('');
  const [message, setMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  
  // Error dialog state
  const [errorDialog, setErrorDialog] = useState<{
    isOpen: boolean;
    title: string;
    message: string;
    variant?: 'error' | 'info';
  }>({
    isOpen: false,
    title: '',
    message: '',
    variant: 'error'
  });

  const resetForm = () => {
    setEmail('');
    setMessage('');
    setSuccess(false);
    setErrorDialog({ isOpen: false, title: '', message: '' });
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const showDialog = (title: string, message: string, variant: 'error' | 'info' = 'error') => {
    setErrorDialog({
      isOpen: true,
      title,
      message,
      variant
    });
  };

  const closeErrorDialog = () => {
    setErrorDialog({ isOpen: false, title: '', message: '' });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validation
    if (!email.trim()) {
      showDialog('Invalid Email', 'Please enter an email address');
      return;
    }

    if (!validateEmail(email)) {
      showDialog('Invalid Email', 'Please enter a valid email address');
      return;
    }

    setIsSubmitting(true);

    try {
      const shareRequest: ShareScanRequest = {
        email: email.trim(),
        message: message.trim() || undefined
        
      };

      const response = await customerApi.shareScan(scene.id, shareRequest);
      
      setSuccess(true);
      
      // Call success callback if provided
      if (onSuccess) {
        onSuccess(response.data);
      }

      // Auto-close after showing success for 2 seconds
      setTimeout(() => {
        handleClose();
      }, 2000);

    } catch (error) {
      console.error('Failed to share scene:', error);
      
      const cleanErrorMessage = extractErrorMessage(error);
      
      // Show different titles based on error content
      let errorTitle = 'Sharing Failed';
      let variant: 'error' | 'info' = 'error';
      if (cleanErrorMessage.toLowerCase().includes('already shared')) {
        errorTitle = 'Already Shared';
        variant = 'info';
      } else if (cleanErrorMessage.toLowerCase().includes('not found')) {
        errorTitle = 'Scene Not Found';
      } else if (cleanErrorMessage.toLowerCase().includes('unauthorized')) {
        errorTitle = 'Permission Denied';
      }
      
      showDialog(errorTitle, cleanErrorMessage, variant);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <>
      <div className="fixed inset-0 z-[1100] overflow-y-auto flex items-center justify-center">
        <div className="flex min-h-full items-center justify-center p-4">
          {/* Backdrop */}
          <div 
            className="fixed inset-0 bg-black/70 transition-opacity"
            onClick={handleClose}
          />
          
          {/* Modal */}
          <div className="relative bg-[#161513] rounded-none shadow-xl w-full max-w-md max-h-[90vh] overflow-y-auto transform transition-all border border-[#0CB4E4]/30 z-50">
            {/* Header */}
            <div className="flex items-center justify-between p-6 border-b border-[#0CB4E4]/20">
              <div>
                <h3 className="text-lg font-semibold text-white">Share Scene</h3>
              </div>
              <button
                onClick={handleClose}
                className="p-2 hover:bg-[#161513] rounded-lg transition-colors text-gray-300 hover:text-white"
                disabled={isSubmitting}
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="px-6 pt-4">
              <p className="text-sm text-gray-300">Share "{scene.name}" with someone</p>
            </div>

            {/* Content */}
            <div className="p-6">
              {success ? (
                <div className="text-center py-4">
                  <div className="w-16 h-16 mx-auto mb-4 bg-[#0CB4E4]/20 rounded-full flex items-center justify-center">
                    <Check className="w-8 h-8 text-[#0CB4E4]" />
                  </div>
                  <h4 className="text-lg font-medium text-white mb-2">Scene Shared Successfully</h4>
                  <p className="text-gray-300">
                    An invitation has been sent to <strong>{email}</strong>
                  </p>
                  <div className="mt-4 p-3 bg-[#161513] rounded-lg border border-[#0CB4E4]/30">
                    <p className="text-sm text-[#0CB4E4]">
                      <Eye className="w-4 h-4 inline mr-1" />
                      They'll be able to view this scene once they sign up or log in.
                    </p>
                  </div>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-5">
                  {/* Email Input */}
                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-white mb-2">
                      Recipient Email Address
                    </label>
                    <div className="relative mb-1">
                      <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                      <input
                        type="email"
                        id="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        placeholder="Enter email address"
                        className="w-full pl-10 pr-4 py-3 rounded-none bg-[#161513] border border-[#0CB4E4]/30 text-white focus:outline-none focus:ring-1 focus:ring-[#0CB4E4] focus:border-[#0CB4E4]"
                        required
                        disabled={isSubmitting}
                        autoComplete="off"
                      />
                    </div>
                    <p className="mt-1 text-xs text-gray-300">
                      If they don't have an account, we'll create a viewer account for them.
                    </p>
                  </div>

                  {/* Message Input */}
                  <div>
                    <label htmlFor="message" className="block text-sm font-medium text-white mb-2">
                      Message (Optional)
                    </label>
                    <textarea
                      id="message"
                      value={message}
                      onChange={(e) => setMessage(e.target.value)}
                      placeholder="Add a personal message..."
                      rows={3}
                      className="w-full px-4 py-3 rounded-none bg-[#161513] border border-[#0CB4E4]/30 text-white focus:outline-none focus:ring-1 focus:ring-[#0CB4E4] focus:border-[#0CB4E4] resize-none"
                      disabled={isSubmitting}
                      maxLength={500}
                    />
                    <p className="mt-1 text-xs text-gray-400">
                      {message.length}/500 characters
                    </p>
                  </div>

                  {/* Scene Info */}
                  <div className="bg-[#161513] rounded-none border border-[#0CB4E4]/30 p-4">
                    <div className="flex items-center gap-3">
                      {scene.thumbnail ? (
                        <div className="w-14 h-14 rounded-none overflow-hidden bg-[#0CB4E4]/10 border border-[#0CB4E4]/30 flex-shrink-0">
                          <img 
                            src={scene.thumbnail} 
                            alt={scene.name}
                            className="w-full h-full object-cover"
                          />
                        </div>
                      ) : (
                        <div className="w-14 h-14 bg-[#0CB4E4]/10 rounded-none flex items-center justify-center border border-[#0CB4E4]/30 flex-shrink-0">
                          <Eye className="w-6 h-6 text-[#0CB4E4]" />
                        </div>
                      )}
                      <div>
                        <h4 className="font-medium text-white">{scene.name}</h4>
                        <p className="text-sm text-gray-300">
                          {scene.quality.imageCount} images • {scene.status}
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Action Buttons */}
                  <div className="flex gap-3 pt-2">
                    <button 
                      type="button"
                      onClick={handleClose}
                      className="flex-1 px-4 py-3 border border-[#0CB4E4]/30 text-white rounded-xl hover:bg-[#161513] transition-colors shadow-md"
                      disabled={isSubmitting}
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={isSubmitting || !email.trim()}
                      className="flex-1 px-4 py-3 bg-[#0CB4E4] text-[#161513] rounded-xl hover:bg-[#0CB4E4]/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors shadow-md flex items-center justify-center gap-2"
                    >
                      {isSubmitting ? (
                        <>
                          <Loader className="w-4 h-4 animate-spin" />
                          Sharing...
                        </>
                      ) : (
                        <>
                          <Send className="w-4 h-4" />
                          Share Scene
                        </>
                      )}
                    </button>
                  </div>
                </form>
              )}
            </div>
          </div>
        </div>
      </div>
      
      {/* Dialog */}
      <Dialog
        isOpen={errorDialog.isOpen}
        title={errorDialog.title}
        message={errorDialog.message}
        variant={errorDialog.variant}
        onClose={closeErrorDialog}
      />
    </>
  );
}
